import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductMediaGalleryRefs
 * @property {HTMLButtonElement[]} thumbnails - Thumbnail buttons (ref="thumbnails[]")
 */

/**
 * Product Media Gallery
 *
 * Desktop vertical: IntersectionObserver syncs thumbnails to scroll position (stacked images).
 * Desktop horizontal: scroll listener on carousel viewport syncs thumbnails (1 image carousel).
 * Mobile: carousel-slider handles everything (arrows, dots, swipe) — thumbnails hidden via CSS.
 *
 * @extends {Component<ProductMediaGalleryRefs>}
 */
class ProductMediaGallery extends Component {
	/** @type {IntersectionObserver|null} */
	#observer = null;

	/** @type {AbortController|null} */
	#abortController = null;

	connectedCallback() {
		super.connectedCallback();
		this.#initDesktopSync();
		this.#initThumbnailSkeletons();
	}

	disconnectedCallback() {
		this.#cleanup();
		super.disconnectedCallback();
	}

	updatedCallback() {
		this.#cleanup();
		this.#initDesktopSync();
		this.#initThumbnailSkeletons();
	}

	#initThumbnailSkeletons() {
		const thumbs = this.querySelectorAll('.gallery__thumb');
		for (const thumb of thumbs) {
			const img = thumb.querySelector('.gallery__thumb-img');
			if (!img) {
				thumb.classList.add('gallery__thumb--loaded');
				continue;
			}
			if (img.complete && img.naturalHeight > 0) {
				thumb.classList.add('gallery__thumb--loaded');
				continue;
			}
			const markLoaded = () => thumb.classList.add('gallery__thumb--loaded');
			img.addEventListener('load', markLoaded, { once: true });
			img.addEventListener('error', markLoaded, { once: true });
		}
	}

	/** @returns {boolean} */
	get #isHorizontal() {
		return this.dataset.galleryLayout === 'horizontal';
	}

	#initDesktopSync() {
		if (this.dataset.showThumbnails !== 'true') return;

		if (this.#isHorizontal) {
			this.#initHorizontalSync();
			return;
		}

		const slides = this.querySelectorAll('.carousel__slide:not([data-clone])');
		if (slides.length === 0) return;

		/** @type {Map<Element, number>} */
		const visibilityMap = new Map();

		this.#observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					visibilityMap.set(entry.target, entry.intersectionRatio);
				}

				let bestIndex = 0;
				let bestRatio = 0;

				let i = 0;
				for (const slide of slides) {
					const ratio = visibilityMap.get(slide) ?? 0;
					if (ratio > bestRatio) {
						bestRatio = ratio;
						bestIndex = i;
					}
					i++;
				}

				this.#updateActiveThumbnail(bestIndex);
			},
			{
				threshold: [0, 0.25, 0.5, 0.75, 1],
			}
		);

		for (const slide of slides) {
			this.#observer.observe(slide);
		}
	}

	#initHorizontalSync() {
		const carousel = this.querySelector('carousel-slider');
		if (!carousel) return;
		const viewport = carousel.querySelector('[ref="viewport"]');
		if (!viewport) return;

		this.#abortController = new AbortController();

		const getActiveIndex = () => {
			const slides = carousel.querySelectorAll('.carousel__slide:not([data-clone])');
			const scrollLeft = viewport.scrollLeft;
			let closest = 0;
			let minDist = Infinity;
			for (let i = 0; i < slides.length; i++) {
				const dist = Math.abs(slides[i].offsetLeft - scrollLeft);
				if (dist < minDist) {
					minDist = dist;
					closest = i;
				}
			}
			return closest;
		};

		viewport.addEventListener('scroll', () => {
			this.#updateActiveThumbnail(getActiveIndex());
		}, { passive: true, signal: this.#abortController.signal });
	}

	/**
	 * Update aria-current on thumbnail buttons to reflect the active slide.
	 * Scrolls the active thumbnail into view within the thumbnail strip ONLY —
	 * never the page. Using `scrollIntoView` walks up to every scrollable
	 * ancestor (including `<html>`), and when the strip is sticky-pinned past
	 * the gallery section, the browser smoothly pulls the page scroll back
	 * to where the thumbnail's layout box is. We avoid that by writing
	 * `scrollTop` directly on the strip.
	 * @param {number} index - The active slide index
	 */
	#updateActiveThumbnail(index) {
		const thumbnails = this.querySelectorAll('.gallery__thumb');

		for (let i = 0; i < thumbnails.length; i++) {
			const isActive = i === index;
			thumbnails[i].setAttribute('aria-current', isActive ? 'true' : 'false');
		}

		const active = thumbnails[index];
		if (active) {
			const strip = active.closest('.gallery__thumbnails');
			if (strip) {
				if (this.#isHorizontal) {
					const offsetLeft = active.offsetLeft;
					const offsetRight = offsetLeft + active.offsetWidth;
					if (offsetLeft < strip.scrollLeft) {
						strip.scrollTo({ left: offsetLeft, behavior: 'smooth' });
					} else if (offsetRight > strip.scrollLeft + strip.clientWidth) {
						strip.scrollTo({ left: offsetRight - strip.clientWidth, behavior: 'smooth' });
					}
				} else {
					const offsetTop = active.offsetTop;
					const offsetBottom = offsetTop + active.offsetHeight;
					if (offsetTop < strip.scrollTop) {
						strip.scrollTo({ top: offsetTop, behavior: 'smooth' });
					} else if (offsetBottom > strip.scrollTop + strip.clientHeight) {
						strip.scrollTo({ top: offsetBottom - strip.clientHeight, behavior: 'smooth' });
					}
				}
			}
		}

		// Sync vertical scroll dots
		const dots = this.querySelectorAll('.gallery__dot');
		for (let i = 0; i < dots.length; i++) {
			dots[i].classList.toggle('gallery__dot--active', i === index);
		}
	}

	/**
	 * Handle thumbnail click — scroll to the clicked slide.
	 * Bound via on:click="/selectSlide?index=N" in HTML.
	 * @param {Object} data - Parsed data from the event binding
	 * @param {number} data.index - The slide index to scroll to
	 */
	selectSlide(data) {
		const index = Number(data.index);

		if (this.#isHorizontal) {
			const carousel = this.querySelector('carousel-slider');
			if (carousel) carousel.scrollTo(index);
			return;
		}

		const slides = this.querySelectorAll('.carousel__slide:not([data-clone])');
		const slide = slides[index];
		if (slide) {
			slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}

	#cleanup() {
		if (this.#observer) {
			this.#observer.disconnect();
			this.#observer = null;
		}
		if (this.#abortController) {
			this.#abortController.abort();
			this.#abortController = null;
		}
	}
}

customElements.define('product-media-gallery', ProductMediaGallery);
